import IconButton from '@/global/components/Button/IconButton'
import { encodeFile } from '@acme/common'
import { Alert } from '@/global/components/Alert'
import { FormFile } from 'global/components/Form'
import IconeAjuda from 'global/components/IconeAjuda'
import Dialog from 'global/guides/Dialog'
import { hasPermission } from 'hooks/useBackendMenu'
import { DownloadIcon } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { planos } from 'service/api'
import { toast } from 'sonner'
import { z } from 'zod'

const IMPORT_MIMES = [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.oasis.opendocument.spreadsheet',
    'text/csv',
]

const ImportarSchema = z.object({
    file: z.instanceof(File, { message: 'Selecione um arquivo XLSX ou CSV' }),
})

type ImportarInput = z.infer<typeof ImportarSchema>

export default function ImportarPlano() {
    const navigate = useNavigate()
    const handleBack = () => navigate('/administracao/planos-precos')

    const { mutateAsync, isLoading } = planos.mutations.useImport({
        invalidateQuery: true,
    })

    async function handleSubmit({ file }: ImportarInput) {
        await mutateAsync({ file: await encodeFile(file) } as any, {
            onSuccess: () => {
                toast.success('Planos importados com sucesso!')
                handleBack()
            },
            onError: (err: any) => {
                toast.error(
                    `Erro ao importar planos: ${
                        err?.response?.data?.message ?? err?.message ?? ''
                    }`,
                )
            },
        })
    }

    return (
        <Dialog.Root
            open={hasPermission(['create', 'Planos e preços'])}
            width="xl"
            loading={isLoading}
            onClose={handleBack}
            onSubmit={handleSubmit}
            schema={ImportarSchema}
        >
            <Dialog.Title>
                Importar planos
                <IconeAjuda>
                    <h4 style={{ textAlign: 'center' }}>
                        Importação XLSX ou CSV{' '}
                        <IconButton
                            title="Baixar arquivo de exemplo"
                            onClick={baixarExemplo}
                        >
                            <DownloadIcon />
                        </IconButton>
                    </h4>
                    <br />O arquivo deve ter a <strong>primeira coluna</strong>{' '}
                    com o limite de funcionários e uma coluna para cada
                    periodicidade. O cabeçalho de cada coluna de preço deve ser
                    um dos valores: weekly, biweekly, monthly, bimonthly,
                    quarterly, biannual, yearly. Os preços ficam em reais (ex.:
                    95,00). Células vazias são ignoradas. No CSV, use{' '}
                    <strong>ponto e vírgula (;)</strong> como separador — é o
                    mesmo formato gerado por <em>Exportar CSV</em>.
                </IconeAjuda>
            </Dialog.Title>
            <Dialog.Content>
                <Alert severity="warning" className="mb-4">
                    A importação <strong>substitui todos os planos</strong>{' '}
                    existentes pelos planos da planilha.
                </Alert>
                <FormFile
                    name="file"
                    label="Planilha XLSX ou CSV"
                    notPreview
                    accept={IMPORT_MIMES}
                />
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton />
                <Dialog.Actions.PrimaryButton>
                    Importar
                </Dialog.Actions.PrimaryButton>
            </Dialog.Actions>
        </Dialog.Root>
    )
}

async function baixarExemplo() {
    const XLSX = await import('xlsx')
    const ws = XLSX.utils.aoa_to_sheet([
        ['limiteFuncionarios', 'monthly', 'quarterly', 'yearly'],
        [10, '95,10', '270,00', '950,00'],
        [20, '180,00', '510,00', '1800,55'],
    ])
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'planos')
    XLSX.writeFile(wb, 'exemplo-importacao-planos.xlsx')
}
